import { Check } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

export interface MessageSystemChipProps
  extends Omit<React.HTMLAttributes<HTMLOutputElement>, "color"> {
  /** Chip body — usually a short status (e.g. `"Workspace aggiornato"`). */
  label: React.ReactNode;
  /** Override the default `Check` glyph (orange). */
  icon?: React.ReactNode;
  /** Hide the leading icon entirely. */
  hideIcon?: boolean;
}

/**
 * MessageSystemChip — inline status chip rendered between bubbles.
 *
 * Maps to `.ws-chip` in `tasks/mockups/ando_v18.html` (lines 701-710):
 * pill-shaped, glassy background, orange check, dim caption text.
 * Used by `system` messages (`"Workspace aggiornato"`, `"Email inviata"`,
 * `"3 fonti connesse"`, …) — short factual updates that don't deserve a
 * full bubble.
 */
export const MessageSystemChip = React.forwardRef<HTMLOutputElement, MessageSystemChipProps>(
  ({ label, icon, hideIcon = false, className, ...rest }, ref) => (
    <output
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1.5 self-start rounded-full border border-hairline bg-card/70 px-2.5 py-1 font-medium text-[11px] text-ink-2 shadow-xs backdrop-blur-sm",
        className,
      )}
      {...(rest as React.HTMLAttributes<HTMLOutputElement>)}
    >
      {!hideIcon ? (
        <span className="text-ando-orange" aria-hidden>
          {icon ?? <Check size={12} strokeWidth={2.25} />}
        </span>
      ) : null}
      <span>{label}</span>
    </output>
  ),
);
MessageSystemChip.displayName = "MessageSystemChip";
